import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { extensionsRoute } from 'src/app/app-routing.module';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { getExtensionTooltip } from 'src/app/pipes/extension-tool-tip-display/extension-tool-tip-display.pipe';
import { getSelectedAccount } from 'src/app/store/select-account/select-account.selector';
import { AddExtensionComponent } from '../../add-extension/containers/add-extension.component';
import { getLastProvisionedDate, getPhoneTypeStatuses } from '../../extension-details/helpers/extension-status-helper';
import {
   getHackedAndFraudSipUserIdsFromExtension,
   getHackedOrFraudPhonesFromExtensionDetail,
   getIsFraudFromExtension,
   getIsHackedFromExtension,
} from '../../extension-details/helpers/hacked-fraud-helper';
import { ExtensionNumberDetail } from '../../extension-details/models/extension-detail-view.model';
import * as ExtensionTabKeys from '../../extension-details/models/extension-tab-mapping.model';
import { ExternalNumbersQuery } from '../../extension-details/queries/external-numbers.graphql';
import {
   ExternalNumberHistories,
   ExternalNumberHistoriesVariables,
} from '../../extension-details/queries/generated/ExternalNumberHistories';
import { navigate } from '../../extension-details/state/extension-detail.actions';
import { ExtensionOrderingOptions, ExtensionRegisterOptions } from '../models/extension-register-options';
import { ExtensionRegisterListItem, ExtensionRegisterResults } from '../models/extension-register-results.model';
import { ExtensionsFilterOption } from '../models/extensions-filter-option';
import { GetExtensions, GetExtensions_extensionsConnection_items } from '../queries/generated/GetExtensions';
import { ExtensionRegisterApiService } from '../services/extension-register-api.service';
import { ExtensionRegisterSessionStorageService } from '../services/extension-register-session-storage.service';
import {
   changeSipUserPasswordFailure,
   changeSipUserPasswordSuccess,
   changeSipUserPasswords,
   createExtension,
   extensionRegisterAdapterActions,
   setOrderingOptionsFromSessionStorage,
} from './extension-register.actions';
import {
   ExtensionRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './extension-register.reducer';
import * as ExtensionRegisterSelectors from './extension-register.selectors';
@Injectable()
export class ExtensionRegisterEffects {
   addExtension$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createExtension),
            withLatestFrom(
               this.store.pipe(select(selectQuickFilters)),
               this.store.pipe(select(ExtensionRegisterSelectors.selectFilters)),
               this.store.pipe(select(ExtensionRegisterSelectors.selectExtensions)),
               this.store.pipe(select(getSelectedAccount))
            ),
            switchMap(([createExtensionTypeOption, quickFilters, filters, extensions, account]) => {
               const extensionNumbers: number[] = [];
               Object.values(extensions).forEach((ext) => {
                  if (ext?.number) {
                     extensionNumbers.push(ext?.number);
                  }
               });

               const accountId = filters.length > 0 ? filters[0].value : null;
               const accountIdNumber: number = Number(accountId) || 0; // Use Number to ensure a number type

               return this.loadAddExtensionData(filters, quickFilters, accountIdNumber).pipe(
                  switchMap((loadSuccessAction) => {
                     const filtersString = 'AccountId = ' + account.accountId;

                     return this.apiService
                        .query<ExternalNumberHistories, ExternalNumberHistoriesVariables>({
                           query: ExternalNumbersQuery,
                           variables: {
                              filters: filtersString,
                           },
                        })
                        .pipe(
                           switchMap((result) => {
                              const externalNumbers = result.data;

                              const dialogRef = this.dialogService.openFormComponent(AddExtensionComponent, {
                                 data: {
                                    createExtensionTypeOption,
                                    loadSuccessAction,
                                    extensionNumbers,
                                    externalNumbers,
                                 },
                                 disableClose: true,
                                 panelClass: ['add-dialog-panel', 'dialog-container'],
                              });

                              this.dialogId = dialogRef.id;

                              return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
                           }),
                           catchError((error) => {
                              return of();
                           })
                        );
                  })
               );
            })
         );
      },
      { dispatch: false }
   );

   loadExtensionList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionRegisterAdapterActions.pageEnter, changeSipUserPasswordSuccess),
         withLatestFrom(
            this.store.pipe(select(ExtensionRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.extensionRegisterSessionStorageService.getExtensionOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   changeSipUserPassword$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(changeSipUserPasswords),
         switchMap((action) => {
            return this.extensionRegisterApiService.changeSipUserPasswords(action.sipUserIds).pipe(
               map(() => {
                  return changeSipUserPasswordSuccess();
               }),
               catchError((error) => {
                  return of(changeSipUserPasswordFailure(error));
               })
            );
         })
      );
   });

   loadExtensionPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(ExtensionRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            const sessionOrderingOptions = this.extensionRegisterSessionStorageService.getExtensionOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(ExtensionRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(ExtensionRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, orderingOptions]) => {
            this.extensionRegisterSessionStorageService.setExtensionOrderingOptions(orderingOptions);
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   navigate$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(navigate),
            tap((action) => {
               const extensionType = this.getExtensionTypeFromId(action.id, action.extensionNumbersWithTypes);
               let tabNavigation = false;
               if (extensionType) tabNavigation = this.determineTabNavigation(extensionType);
               const currentUrl = window.location.pathname;
               const currentTab = currentUrl.substring(currentUrl.lastIndexOf('/') + 1);

               if (tabNavigation)
                  this.router.navigateByUrl(`/${extensionsRoute}/${action.id}/${currentTab}`, {
                     replaceUrl: true,
                  });
               else this.router.navigateByUrl(`/${extensionsRoute}/${action.id}`, { replaceUrl: true });
            })
         ),
      { dispatch: false }
   );

   loadExtensionIdPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionRegisterAdapterActions.loadIdPage),
         withLatestFrom(
            this.store.pipe(select(ExtensionRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, endCursor, orderingOptions]) => {
            const sessionOrderingOptions = this.extensionRegisterSessionStorageService.getExtensionOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewExtensionIdPage(endCursor, quickFilters, filters, orderingOptions);
         })
      );
   });

   dialogId: string = '';

   constructor(
      private actions$: Actions,
      private store: Store<ExtensionRegisterState>,
      private dialogService: LcsDialogService,
      private apiService: LcsApiService,
      private extensionRegisterApiService: ExtensionRegisterApiService,
      private extensionRegisterSessionStorageService: ExtensionRegisterSessionStorageService,
      private router: Router
   ) {}

   private convertGetExtensionsResultToLoadSuccessProps(getExtensions: GetExtensions): ExtensionRegisterResults {
      return {
         endCursor: getExtensions.extensionsConnection.pageInfo.endCursor,
         hasNextPage: getExtensions.extensionsConnection.pageInfo.hasNextPage,
         items: getExtensions.extensionsConnection.items.map(this.convertExtensionRegisterListItem),
         totalCount: getExtensions.extensionsConnection.totalCount,
      };
   }

   private convertExtensionRegisterListItem(
      getExtensionItem: GetExtensions_extensionsConnection_items
   ): ExtensionRegisterListItem {
      return {
         extensionId: getExtensionItem.extensionId,
         name: getExtensionItem.name,
         number: getExtensionItem.number,
         defaultPhoneNumber: getExtensionItem.extensionPhone?.defaultPhoneNumber?.phoneNumber ?? '',
         type: {
            displayName: getExtensionItem.extensionType?.displayName ?? '',
         },
         isHacked: getIsHackedFromExtension(getExtensionItem),
         isFraud: getIsFraudFromExtension(getExtensionItem),
         phoneTypeStatuses: getPhoneTypeStatuses(getExtensionItem),
         lastProvisionedDate: getLastProvisionedDate(getExtensionItem),
         hackedAndFraudSipUserIds: getHackedAndFraudSipUserIdsFromExtension(getExtensionItem),
         hackedOrFraudPhones: getHackedOrFraudPhonesFromExtensionDetail(getExtensionItem),
         account: {
            defaultAreaCode: getExtensionItem.account?.defaultAreaCode,
         },
         extensionTooltip: getExtensionTooltip(getExtensionItem),
      };
   }

   private loadNewData(
      filters: ExtensionsFilterOption[],
      quickFilters: ExtensionRegisterOptions | null = null,
      orderingOptions: ExtensionOrderingOptions[] | null = null
   ) {
      return this.extensionRegisterApiService.getExtensions(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return extensionRegisterAdapterActions.loadSuccess(
               this.convertGetExtensionsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(extensionRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadAddExtensionData(
      filters: ExtensionsFilterOption[],
      quickFilters: ExtensionRegisterOptions | null = null,
      accountId: number | null
   ) {
      return this.extensionRegisterApiService.getExtensionsForAdd(null, filters, quickFilters, null, accountId).pipe(
         map((result) => {
            return result.data;
         }),
         catchError((error) => {
            return of(extensionRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: ExtensionsFilterOption[],
      quickFilters: ExtensionRegisterOptions | null = null,
      orderingOptions: ExtensionOrderingOptions[] | null
   ) {
      return this.extensionRegisterApiService.getExtensions(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return extensionRegisterAdapterActions.loadPageSuccess(
               this.convertGetExtensionsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(extensionRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private loadNewExtensionIdPage(
      endCursor: string | null,
      quickFilters: ExtensionRegisterOptions | null = null,
      filters: ExtensionsFilterOption[] | null = null,
      orderingOptions: ExtensionOrderingOptions[] | null = null
   ) {
      return this.extensionRegisterApiService.getExtensionIds(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return extensionRegisterAdapterActions.loadIdPageSuccess({
               ids: result.data.extensionsConnection.items.map((i) => i.extensionId),
            });
         }),
         catchError((error) => {
            return of(extensionRegisterAdapterActions.loadIdPageFailure(error));
         })
      );
   }

   private getExtensionTypeFromId(
      id: number | string,
      extensionTypesWithNumbers: ExtensionNumberDetail[]
   ): string | undefined {
      const match = extensionTypesWithNumbers.find((ext) => ext.extensionId === id);
      return match?.type;
   }

   private determineTabNavigation(extensionType: string): boolean {
      const currentUrl = window.location.pathname;
      const currentTab = currentUrl.substring(currentUrl.lastIndexOf('/') + 1); // gets the last /xxx part

      switch (extensionType) {
         case 'Phone':
            return this.isTabValid<ExtensionTabKeys.ExtensionPhoneTabKey>(currentTab, [
               'extension',
               'phones',
               'inboundcallrules',
               'calllog',
               'mailbox',
            ]);
         case 'AutoAttendant':
            return this.isTabValid<ExtensionTabKeys.ExtensionAutoAttendantTabKey>(currentTab, [
               'extension',
               'prompts',
               'keyinformation',
               'inboundcallrules',
               'calllog',
            ]);
         case 'HuntGroup':
            return this.isTabValid<ExtensionTabKeys.ExtensionHuntGroupTabKey>(currentTab, [
               'extension',
               'inboundcallrules',
               'calllog',
            ]);
         case 'Fax':
            return this.isTabValid<ExtensionTabKeys.ExtensionFaxTabKey>(currentTab, [
               'extension',
               'whitelistedemails',
               'outgoingfaxes',
               'incomingfaxes',
            ]);
         case 'ExternalForward':
            return this.isTabValid<ExtensionTabKeys.ExtensionExternalForawardTabKey>(currentTab, [
               'extension',
               'inboundcallrules',
               'calllog',
            ]);
         case 'SmartVoicemail':
         case 'VoiceMailbox':
            return this.isTabValid<ExtensionTabKeys.ExtensionVoicemailBoxTabKey>(currentTab, [
               'extension',
               'calllog',
               'mailbox',
            ]);
         case 'CallQueue':
            return this.isTabValid<ExtensionTabKeys.ExtensionCallQueueTabKey>(currentTab, [
               'extension',
               'queuecalls',
               'periodicannouncements',
               'inboundcallrules',
               'calllog',
            ]);
         case 'PagingGroup':
            return this.isTabValid<ExtensionTabKeys.ExtensionPagingGroupTabKey>(currentTab, [
               'extension',
               'inboundcallrules',
               'calllog',
            ]);
         case 'FindMeFollowMe':
            return this.isTabValid<ExtensionTabKeys.ExtensionFindMeFollowMeTabKey>(currentTab, [
               'extension',
               'steps',
               'inboundcallrules',
               'calllog',
            ]);
         default:
            return false;
      }
   }

   private isTabValid<T extends string>(tab: string, validTabs: T[]): tab is T {
      return validTabs.includes(tab as T);
   }
}
